import React, { useEffect, useMemo, useState } from "react";
import Parse from "parse";
import "./StatisticsModal.css";

export default function StatisticsModal({ idNumber, langString = "nl", onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [myScoreForThisId, setMyScoreForThisId] = useState(null);
  const [isEpictetusViewer, setIsEpictetusViewer] = useState(false);

  const labels = useMemo(() => {
    if (langString === "nl") {
      return {
        title: "Scores",
        intro: "",
        averageLabel: "Gemiddelde score:",
        yourScoreLabel: "Jouw score:",
        usersLabel: "Users:",
        loading: "Statistieken laden...",
        errorPrefix: "Kon statistieken niet laden:",
        empty: "Nog geen data voor deze WordHit.",
        close: "Sluiten",
        users: "Users",
        you: "jij",
      };
    }
    return {
      title: "Scores",
      intro: "",
      averageLabel: "Average score:",
      yourScoreLabel: "Your score:",
      usersLabel: "Users:",
      loading: "Loading statistics...",
      errorPrefix: "Could not load statistics:",
      empty: "No data yet for this WordHit.",
      close: "Close",
      users: "users",
      you: "you",
    };
  }, [langString]);

  useEffect(() => {
    let cancelled = false;
    async function loadMe() {
      setMyScoreForThisId(null);
      setIsEpictetusViewer(false);
      if (!Number.isFinite(idNumber)) return;
      const user = Parse.User.current();

      // First: try local finished score for this id (stored by WordHit.jsx)
      // This works for both anonymous and logged-in users, and also for older puzzles.
      try {
        const raw = window?.localStorage?.getItem("finishedGameClicksLeft");
        if (raw) {
          const parsed = JSON.parse(raw);
          const localScore = parsed?.[String(idNumber)];
          if (
            localScore !== undefined &&
            localScore !== null &&
            Number.isFinite(localScore) &&
            Number(localScore) >= 0 &&
            Number(localScore) <= 10
          ) {
            setMyScoreForThisId(Number(localScore));
          }
        }
      } catch (e) {
        // ignore
      }

      // If not logged in: nothing else we can fetch
      if (!user) return;
      try {
        // Fetch latest values (safe, small payload)
        const fresh = await user.fetch();
        if (cancelled) return;
        const viewerName =
          fresh.get("name") ||
          fresh.get("displayName") ||
          fresh.get("username") ||
          "";
        setIsEpictetusViewer(String(viewerName).trim() === "Epictetus");
        const lastPlayedId = fresh.get("lastPlayedId");
        const lastScore = fresh.get("lastScore");
        const played =
          lastPlayedId !== undefined &&
          lastPlayedId !== null &&
          Number.isFinite(lastPlayedId) &&
          Number(lastPlayedId) === Number(idNumber);
        const scoreOk =
          lastScore !== undefined &&
          lastScore !== null &&
          Number.isFinite(lastScore) &&
          Number(lastScore) >= 0 &&
          Number(lastScore) <= 10;
        // Only fill from server "lastScore" if local score wasn't found
        if (myScoreForThisId === null && played && scoreOk) {
          setMyScoreForThisId(Number(lastScore));
        }
      } catch (e) {
        // Fail open: just don't highlight if we can't fetch
      }
    }
    loadMe();
    return () => {
      cancelled = true;
    };
  }, [idNumber, myScoreForThisId]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!Number.isFinite(idNumber)) return;
      setLoading(true);
      setError("");
      try {
        const res = await Parse.Cloud.run("getScoreDistributionForId", { idNumber });
        if (cancelled) return;
        setData(res);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [idNumber]);

  const rows = useMemo(() => {
    const total = Number(data?.total) || 0;
    const p = data?.percentages || {};
    const c = data?.counts || {};
    const out = [];
    for (let score = 10; score >= 0; score--) {
      const pct = Number(p[score]) || 0;
      const count = Number(c[score]) || 0;
      out.push({
        score,
        pct: total > 0 ? Math.round(pct) : 0,
        count,
      });
    }
    return out;
  }, [data]);

  // Find max percentage for relative scaling
  const maxPercentage = useMemo(() => {
    if (!rows || rows.length === 0) return 100;
    return Math.max(...rows.map(r => r.pct), 1); // At least 1 to avoid division by zero
  }, [rows]);

  // Calculate relative bar widths (max percentage = 100% width)
  const rowsWithRelativeWidth = useMemo(() => {
    return rows.map(r => ({
      ...r,
      relativeWidth: maxPercentage > 0 ? (r.pct / maxPercentage) * 100 : 0,
    }));
  }, [rows, maxPercentage]);

  const totalUsers = Number(data?.total) || 0;
  const averageScore = useMemo(() => {
    const counts = data?.counts || {};
    const total = Number(data?.total) || 0;
    if (total <= 0) return null;
    let sum = 0;
    for (let s = 0; s <= 10; s++) {
      const c = Number(counts[s]) || 0;
      sum += s * c;
    }
    return sum / total;
  }, [data]);

  const averageScore1dpNoRound = useMemo(() => {
    if (averageScore === null || !Number.isFinite(averageScore)) return null;
    // 0..10 domain, so simple floor works for "no rounding" at 1 decimal.
    const truncated = Math.floor(averageScore * 10) / 10;
    return truncated;
  }, [averageScore]);

  return (
    <>
      <div
        className="stats-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            onClose?.();
          }
        }}
      />
      <div className="stats-modal" role="dialog" aria-modal="true" aria-label={labels.title}>
        <button className="stats-close" onClick={onClose} aria-label={labels.close}>
          ✕
        </button>

        <div className="stats-content">
          <div className="stats-title">{labels.title}</div>
          {labels.intro ? <div className="stats-intro">{labels.intro}</div> : null}
          {data && (
            <>
              <div className="stats-subline">
                {labels.averageLabel}{" "}
                <strong>
                  {averageScore1dpNoRound === null ? "-" : averageScore1dpNoRound.toFixed(1)}
                </strong>{" "}
                {isEpictetusViewer && (
                  <>
                    {" "}
                    (<strong>{totalUsers}</strong>)
                  </>
                )}
              </div>
              {myScoreForThisId !== null && Number.isFinite(myScoreForThisId) && (
                <div className="stats-subline">
                  {labels.yourScoreLabel} <strong>{myScoreForThisId}</strong>
                </div>
              )}
            </>
          )}

          {loading && <div className="stats-status">{labels.loading}</div>}
          {!loading && error && (
            <div className="stats-status stats-status--error">
              {labels.errorPrefix} {error}
            </div>
          )}
          {!loading && !error && data && totalUsers === 0 && (
            <div className="stats-status">{labels.empty}</div>
          )}

          <div className="stats-list" role="list">
            {loading ? (
              // Show empty rows during loading to maintain modal size
              Array.from({ length: 11 }, (_, i) => (
                <div className="stats-row" key={`loading-${i}`}>
                  <div className="stats-row-left">{10 - i}</div>
                  <div className="stats-row-bar">
                    <div className="stats-bar-track" aria-hidden="true">
                      <div className="stats-bar-fill" style={{ width: "0%" }} />
                    </div>
                    <div className="stats-bar-value">-</div>
                  </div>
                </div>
              ))
            ) : !error && data && totalUsers > 0 ? (
              rowsWithRelativeWidth.map((r) => (
                <div
                  className={`stats-row${myScoreForThisId === r.score ? " stats-row--me" : ""}`}
                  role="listitem"
                  key={`score-${r.score}`}
                >
                  <div className="stats-row-left">
                    {r.score}
                  </div>
                  <div className="stats-row-bar">
                    <div className="stats-bar-track" aria-hidden="true">
                      <div
                        className="stats-bar-fill"
                        style={{ width: `${r.relativeWidth}%` }}
                      />
                    </div>
                    <div className="stats-bar-value">
                      {r.pct}<span className="stats-bar-value-percent">%</span>
                      {isEpictetusViewer && (
                        <span className="stats-row-count">({r.count})</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Show empty rows when no data
              Array.from({ length: 11 }, (_, i) => (
                <div className="stats-row" key={`empty-${i}`}>
                  <div className="stats-row-left">{10 - i}</div>
                  <div className="stats-row-bar">
                    <div className="stats-bar-track" aria-hidden="true">
                      <div className="stats-bar-fill" style={{ width: "0%" }} />
                    </div>
                    <div className="stats-bar-value">-</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

